import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Download } from "lucide-react";

const COLORS = ["#10b981", "#06b6d4", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#84cc16"];

const fmtDate = (d) => d.toISOString().slice(0, 10);

const presets = {
  "7": 7,
  "30": 30,
  "90": 90,
};

const AdminDashboard = () => {
  const { t } = useTranslation();
  const [preset, setPreset] = useState("30");
  const today = new Date();
  const [start, setStart] = useState(fmtDate(new Date(today.getTime() - 30 * 86400000)));
  const [end, setEnd] = useState(fmtDate(today));
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);

  const setPresetRange = (days) => {
    setPreset(String(days));
    const e = new Date();
    const s = new Date(e.getTime() - days * 86400000);
    setStart(fmtDate(s));
    setEnd(fmtDate(e));
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start: `${start}T00:00:00`, end: `${end}T23:59:59` };
      const [s, u, l, i] = await Promise.all([
        axios.get("/admin/stats", { params, withCredentials: true }),
        axios.get("/admin/users", { withCredentials: true }),
        axios.get("/admin/login-logs", { params, withCredentials: true }),
        axios.get("/admin/interactions", { params, withCredentials: true }),
      ]);
      setStats(s.data);
      setUsers(u.data.users || []);
      setLogs(l.data.logs || []);
      setInteractions(i.data.interactions || []);
      setNeedsBootstrap(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setNeedsBootstrap(true);
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleBootstrap = async () => {
    try {
      await axios.post("/admin/bootstrap", {}, { withCredentials: true });
      toast.success("You are now an admin!");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bootstrap failed");
    }
  };

  const downloadCsv = async (path, filename) => {
    try {
      const params = { start: `${start}T00:00:00`, end: `${end}T23:59:59` };
      const res = await axios.get(path, {
        params,
        responseType: "blob",
        withCredentials: true,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  if (needsBootstrap) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-4">
        <Card className="p-8 text-center" data-testid="admin-bootstrap-card">
          <h1 className="text-2xl font-bold mb-3">{t("admin.title")}</h1>
          <p className="text-gray-600 mb-6">{t("admin.bootstrapHint")}</p>
          <Button onClick={handleBootstrap} className="btn-primary" data-testid="admin-bootstrap-btn">
            {t("admin.bootstrap")}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-1">{t("admin.title")}</h1>
        <p className="text-gray-600">{t("admin.subtitle")}</p>
      </div>

      {/* Date controls */}
      <Card className="p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="admin-date-controls">
        <div className="flex gap-2">
          <Button
            variant={preset === "7" ? "default" : "outline"}
            onClick={() => setPresetRange(7)}
            data-testid="preset-7"
          >
            {t("admin.last7")}
          </Button>
          <Button
            variant={preset === "30" ? "default" : "outline"}
            onClick={() => setPresetRange(30)}
            data-testid="preset-30"
          >
            {t("admin.last30")}
          </Button>
          <Button
            variant={preset === "90" ? "default" : "outline"}
            onClick={() => setPresetRange(90)}
            data-testid="preset-90"
          >
            {t("admin.last90")}
          </Button>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Start</label>
            <Input
              type="date"
              value={start}
              onChange={(e) => {
                setStart(e.target.value);
                setPreset("custom");
              }}
              data-testid="admin-start-date"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">End</label>
            <Input
              type="date"
              value={end}
              onChange={(e) => {
                setEnd(e.target.value);
                setPreset("custom");
              }}
              data-testid="admin-end-date"
            />
          </div>
          <Button onClick={fetchAll} className="btn-primary" data-testid="admin-apply-btn">
            {t("admin.apply")}
          </Button>
        </div>
        <div className="flex gap-2 ml-auto">
          <Button
            variant="outline"
            size="sm"
            data-testid="export-logins-csv"
            onClick={() => downloadCsv(`/admin/export/login-logs.csv`, "login_logs.csv")}
          >
            <Download className="w-4 h-4 mr-1" /> Logins CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="export-interactions-csv"
            onClick={() => downloadCsv(`/admin/export/interactions.csv`, "interactions.csv")}
          >
            <Download className="w-4 h-4 mr-1" /> Interactions CSV
          </Button>
        </div>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        {[
          { key: "users", label: "totalUsers", testid: "stat-total-users" },
          { key: "new_users", label: "newUsers", testid: "stat-new-users" },
          { key: "logins", label: "totalLogins", testid: "stat-logins" },
          { key: "active_users", label: "activeUsers", testid: "stat-active" },
          { key: "interactions", label: "interactions", testid: "stat-interactions" },
          { key: "at_risk_users", label: "atRisk", testid: "stat-at-risk", danger: true },
        ].map((s) => (
          <Card key={s.key} className={`p-4 ${s.danger && stats?.totals?.[s.key] > 0 ? "border-red-300 bg-red-50" : ""}`} data-testid={s.testid}>
            <div className="text-xs text-gray-500 mb-1">{t(`admin.${s.label}`)}</div>
            <div className={`text-3xl font-bold ${s.danger && stats?.totals?.[s.key] > 0 ? "text-red-700" : "text-blue-700"}`}>
              {loading ? "..." : stats?.totals?.[s.key] ?? 0}
            </div>
          </Card>
        ))}
      </div>

      {/* At-Risk Users alert panel */}
      {stats?.at_risk_users && stats.at_risk_users.length > 0 && (
        <Card className="p-5 mb-6 border-2 border-red-300 bg-red-50" data-testid="at-risk-panel">
          <div className="flex items-start gap-3">
            <div className="text-red-600 mt-0.5">⚠️</div>
            <div className="flex-1">
              <h3 className="font-bold text-red-700 mb-2">{t("admin.atRiskTitle")}</h3>
              <p className="text-xs text-red-700 mb-3">{t("admin.atRiskDesc")}</p>
              <div className="space-y-2">
                {stats.at_risk_users.slice(0, 10).map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between bg-white rounded-lg p-3 border border-red-200"
                    data-testid={`at-risk-row-${u.id}`}
                  >
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">{u.name || "—"}</div>
                      <div className="text-xs text-gray-600">{u.email}</div>
                    </div>
                    <div className="text-right">
                      <Badge variant="destructive" className="mb-1">{u.crisis_count} flags</Badge>
                      <div className="text-xs text-gray-500">
                        {u.last_crisis_at ? new Date(u.last_crisis_at).toLocaleString() : "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Tabs defaultValue="charts" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="charts" data-testid="admin-tab-charts">
            {t("admin.chartsTab")}
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">
            {t("admin.usersTab")}
          </TabsTrigger>
          <TabsTrigger value="logins" data-testid="admin-tab-logins">
            {t("admin.loginsTab")}
          </TabsTrigger>
          <TabsTrigger value="events" data-testid="admin-tab-events">
            {t("admin.eventsTab")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="charts">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-4" data-testid="chart-logins">
              <h3 className="font-semibold mb-3">{t("admin.loginsOverTime")}</h3>
              {stats?.logins_daily?.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={stats.logins_daily}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-500 text-sm">{t("admin.noData")}</p>
              )}
            </Card>
            <Card className="p-4" data-testid="chart-interactions">
              <h3 className="font-semibold mb-3">{t("admin.interactionsOverTime")}</h3>
              {stats?.interactions_daily?.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={stats.interactions_daily}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#06b6d4" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-500 text-sm">{t("admin.noData")}</p>
              )}
            </Card>
            <Card className="p-4" data-testid="chart-event-types">
              <h3 className="font-semibold mb-3">{t("admin.byEventType")}</h3>
              {stats?.by_event_type?.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={stats.by_event_type}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="event_type" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-500 text-sm">{t("admin.noData")}</p>
              )}
            </Card>
            <Card className="p-4" data-testid="chart-login-method">
              <h3 className="font-semibold mb-3">{t("admin.byLoginMethod")}</h3>
              {stats?.by_login_method?.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={stats.by_login_method}
                      dataKey="count"
                      nameKey="method"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label
                    >
                      {stats.by_login_method.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-500 text-sm">{t("admin.noData")}</p>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <Card className="p-4 overflow-x-auto" data-testid="admin-users-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">{t("admin.name")}</th>
                  <th className="py-2 pr-4">{t("admin.email")}</th>
                  <th className="py-2 pr-4">{t("admin.role")}</th>
                  <th className="py-2 pr-4">{t("admin.provider")}</th>
                  <th className="py-2 pr-4">{t("admin.createdAt")}</th>
                  <th className="py-2 pr-4">{t("admin.lastLogin")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b" data-testid={`user-row-${u.id}`}>
                    <td className="py-2 pr-4">{u.name}</td>
                    <td className="py-2 pr-4">{u.email}</td>
                    <td className="py-2 pr-4">
                      <Badge variant={u.role === "admin" ? "default" : "outline"}>{u.role}</Badge>
                    </td>
                    <td className="py-2 pr-4">{u.auth_provider}</td>
                    <td className="py-2 pr-4">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="py-2 pr-4">{u.last_login ? new Date(u.last_login).toLocaleString() : "—"}</td>
                  </tr>
                ))}
                {!users.length && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-gray-500">
                      {t("admin.noData")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="logins">
          <Card className="p-4 overflow-x-auto" data-testid="admin-logins-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">{t("admin.when")}</th>
                  <th className="py-2 pr-4">{t("admin.email")}</th>
                  <th className="py-2 pr-4">{t("admin.method")}</th>
                  <th className="py-2 pr-4">{t("admin.ip")}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b">
                    <td className="py-2 pr-4">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="py-2 pr-4">{l.email}</td>
                    <td className="py-2 pr-4">
                      <Badge variant="outline">{l.method}</Badge>
                    </td>
                    <td className="py-2 pr-4">{l.ip || "—"}</td>
                  </tr>
                ))}
                {!logs.length && (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-gray-500">
                      {t("admin.noData")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="events">
          <Card className="p-4 overflow-x-auto" data-testid="admin-events-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">{t("admin.when")}</th>
                  <th className="py-2 pr-4">{t("admin.eventType")}</th>
                  <th className="py-2 pr-4">{t("admin.metadata")}</th>
                </tr>
              </thead>
              <tbody>
                {interactions.map((i) => (
                  <tr key={i.id} className="border-b">
                    <td className="py-2 pr-4">{new Date(i.created_at).toLocaleString()}</td>
                    <td className="py-2 pr-4">
                      <Badge variant="outline">{i.event_type}</Badge>
                    </td>
                    <td className="py-2 pr-4 text-xs font-mono text-gray-600">
                      {i.metadata && Object.keys(i.metadata).length ? JSON.stringify(i.metadata) : "—"}
                    </td>
                  </tr>
                ))}
                {!interactions.length && (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-gray-500">
                      {t("admin.noData")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
